import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, cn, Flex, Icon, Text } from '@kognitos/lattice';

import type { TRelatedOutputsContext } from '@/modules/idp/types';

/**
 * Props for RainReviewCard.
 */
export interface IRainReviewCardProps {
  /** Number of fields surfaced for review (found + missing). */
  fieldCount: number;
  /** Review context — drives the title/icon, mirroring the astral flow. */
  context?: TRelatedOutputsContext;
  /**
   * `pending` (default) is the interactive trigger that opens the IDP dialog.
   * `resolved` is the read-only marker shown after the review was submitted, so
   * a settled review reads as done instead of a stale "Waiting for review" card.
   */
  variant?: 'pending' | 'resolved';
  /** Opens the shared IDP dialog. Ignored for the resolved variant. */
  onClick?: () => void;
}

/**
 * Trigger / marker card for a document-backed `request_review` (IDP review) in
 * the rain chat. While pending it opens the shared IDP document dialog so the
 * rain flow matches the non-rain experience; once resolved it collapses to a
 * non-interactive "Review submitted" marker. The fields-only "Provide missing
 * values" case was removed from the design (MAN-3747). Kept rain-local to avoid
 * a rain → automation-run import.
 */
export function RainReviewCard({
  fieldCount,
  context,
  variant = 'pending',
  onClick,
}: IRainReviewCardProps) {
  const { t } = useTranslation('outputs');

  const isResolved = variant === 'resolved';

  const fieldLabel =
    fieldCount === 1
      ? t('outputs.idp.fieldCount')
      : t('outputs.idp.fieldsCount');

  const title = useMemo(() => {
    if (isResolved) return t('outputs.idp.exception.reviewSubmitted');
    switch (context) {
      case 'analyze_outputs':
        return t('outputs.idp.exception.analyzeTitle');
      case 'manual_action_required':
        return t('outputs.idp.exception.manualActionTitle');
      case 'need_information':
      default:
        return t('outputs.idp.exception.missingTitle');
    }
  }, [isResolved, context, t]);

  const iconType = isResolved
    ? 'CircleCheck'
    : context === 'analyze_outputs'
      ? 'Search'
      : 'List';

  if (isResolved) {
    return (
      <div
        className="bg-card rounded-lg p-4 border border-border"
        data-testid="rain-review-card"
        data-variant="resolved"
      >
        <Flex direction="row" align="center" gap={2}>
          <div className="p-2 rounded-md bg-success/10">
            <Icon type={iconType} size="sm" className="text-success" />
          </div>
          <Flex direction="column" gap={0}>
            <Text weight="medium" level="small">
              {title}
            </Text>
            <Text color="muted" level="small">
              {fieldCount} {fieldLabel}
            </Text>
          </Flex>
        </Flex>
      </div>
    );
  }

  // A DS Button (semantic <button>) so Enter/Space activation, focus ring, and
  // role come for free — no manual onKeyDown / role / tabIndex, and no invalid
  // nested button for the chevron.
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      aria-label={title}
      data-testid="rain-review-card"
      data-variant="pending"
      className={cn(
        'h-auto w-full justify-between gap-3 rounded-lg border border-border bg-card p-4 text-left',
        'hover:bg-accent/50',
      )}
    >
      <Flex direction="row" align="center" gap={2} className="min-w-0 flex-1">
        <div className="p-2 rounded-md bg-primary/10 shrink-0">
          <Icon type={iconType} size="sm" className="text-primary" />
        </div>
        <Flex direction="column" gap={0} align="start" className="min-w-0">
          <Text weight="medium" level="small" className="truncate max-w-full">
            {title}
          </Text>
          <Text color="muted" level="small">
            {fieldCount} {fieldLabel}
          </Text>
        </Flex>
      </Flex>
      <Icon
        type="ChevronRight"
        size="sm"
        className="text-muted-foreground shrink-0"
      />
    </Button>
  );
}
